<script setup>
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { trialCardDismissed, trialExtendClicked } from "@/analytics/events";
import { trackEvent } from "@/analytics/track";
import { useAppStore } from "@/stores";
import { daysUntil, expiryText } from "@/utils/dates";

import BaseButton from "./BaseButton.vue";
import SvgIcon from "./SvgIcon.vue";

const store = useAppStore();
const { isAdmin, isTraining, license, intercomAvailable } = storeToRefs(store);
const expireDays = () => daysUntil(license.value.expiryDate);

const TRIAL_DISMISS_KEY = "trial-card-dismissed-at";
const TRIAL_DISMISS_DURATION = 24 * 60 * 60 * 1000;
const trialDismissedAt = ref(Number(localStorage.getItem(TRIAL_DISMISS_KEY)) || 0);
const trialDismissed = computed(() => Date.now() - trialDismissedAt.value < TRIAL_DISMISS_DURATION);

// Show only to trial admins (not training) from the last 15 days on, also once expired,
// when Intercom is reachable for the CTA. Hidden for 24h after the user dismisses it.
const showCard = () =>
  isAdmin.value &&
  !isTraining.value &&
  intercomAvailable.value &&
  license.value?.type === "trial" &&
  expireDays() !== null &&
  expireDays() <= 15 &&
  !trialDismissed.value;

function trialDaysLabel() {
  return expiryText(expireDays(), "Trial ends");
}

function dismiss() {
  trackEvent(trialCardDismissed());
  trialDismissedAt.value = Date.now();
  localStorage.setItem(TRIAL_DISMISS_KEY, String(trialDismissedAt.value));
}

function extendTrial() {
  trackEvent(trialExtendClicked());
  window.Intercom?.("showNewMessage");
}
</script>

<template>
  <div v-if="showCard()" class="trial-extension-card">
    <button type="button" class="trial-dismiss" aria-label="Dismiss" @click="dismiss">
      <SvgIcon name="system/close" width="16" height="16" />
    </button>
    <span class="trial-badge" aria-hidden="true">
      <SvgIcon name="general/gift" width="18" height="18" />
    </span>
    <p class="trial-meta">Free trial · {{ trialDaysLabel() }}</p>
    <p class="trial-hero">
      <span class="trial-hero-amount">+3 months</span>
      <span class="trial-hero-label">free</span>
    </p>
    <p class="trial-text">Reach out and we'll extend your trial for you.</p>
    <BaseButton size="small" full-width @click="extendTrial">Let's chat</BaseButton>
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;
@use "@/styles/mixins/typography";

.trial-extension-card {
  position: relative;
  background-color: c.$background-ui;
  border: 1px solid c.$border-subtle;
  border-radius: 12px;
  padding: 16px 14px;
  overflow: hidden;
  box-shadow: 0 1px 2px p.$black-alpha-5;

  .trial-dismiss {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: none;
    cursor: pointer;
    color: c.$text-subtle;
    transition: background-color 100ms ease;

    &:hover {
      background-color: p.$black-alpha-5;
      color: c.$main;
    }
  }

  .trial-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-bottom: 12px;
    border-radius: 9px;
    background-color: c.$orange;
    color: c.$white;
  }

  .trial-meta {
    @include typography.caption("bold");

    margin: 0 0 4px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: c.$text-subtle;
  }

  .trial-hero {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin: 0 0 8px;

    .trial-hero-amount {
      @include typography.heading;

      color: c.$main;
    }

    .trial-hero-label {
      @include typography.medium("semi-bold");

      color: c.$main;
    }
  }

  .trial-text {
    @include typography.small("regular");

    margin: 0 0 14px;
    line-height: 1.3;
    color: c.$text-subtle;
  }

  :deep(.base-button) {
    position: relative;
  }
}
</style>
